import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Group, Modal, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle } from "@tabler/icons-react";
import { useDiscountPresets } from "../../../context/DiscountPresetsContext";
import { useResponsive } from "../../../context/ResponsiveContext";
import DiscountValueFields from "../../common/DiscountValueFields";
import {
  DiscountPreset,
  DiscountType,
  MAX_DISCOUNT_PRESET_NAME_LENGTH,
} from "../../../types/discounts";
import {
  isDuplicatePresetNameError,
  validateDiscountAmount,
  validateDiscountName,
} from "../../../utils/discountPresetUtils";
import { notifySuccess } from "../../../utils/notify";

export interface DiscountPresetFormModalProps {
  opened: boolean;
  onClose: () => void;
  /** The preset being edited, or null to create a new one. */
  preset: DiscountPreset | null;
}

interface DiscountPresetFormValues {
  name: string;
  discount_type: DiscountType;
  amount: number | string;
}

const EMPTY_VALUES: DiscountPresetFormValues = {
  name: "",
  discount_type: "Percentage",
  amount: "",
};

/**
 * Add / edit a discount preset. Saves through the shared
 * DiscountPresetsContext, so the change appears in Settings and in any open
 * apply-discount modal without a refetch. A duplicate name (backend 409) is
 * surfaced on the Name field; any other failure shows an inline alert.
 */
export default function DiscountPresetFormModal({
  opened,
  onClose,
  preset,
}: DiscountPresetFormModalProps) {
  const { createPreset, updatePreset } = useDiscountPresets();
  const { isMobile } = useResponsive();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<DiscountPresetFormValues>({
    initialValues: EMPTY_VALUES,
    validate: {
      name: (value) => validateDiscountName(value),
      amount: (value, values) =>
        validateDiscountAmount(values.discount_type, value),
    },
  });

  // `form` is re-created each render; mirror it so the open effect only
  // re-runs when the modal opens or the target preset changes.
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    if (!opened) return;
    setSubmitError(null);
    formRef.current.setValues(
      preset
        ? {
            name: preset.name,
            discount_type: preset.discount_type,
            amount: preset.amount,
          }
        : EMPTY_VALUES,
    );
    formRef.current.clearErrors();
    formRef.current.resetDirty();
  }, [opened, preset]);

  const handleSubmit = async (values: DiscountPresetFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    const body = {
      name: values.name.trim(),
      discount_type: values.discount_type,
      amount: Number(values.amount),
    };
    try {
      if (preset) {
        await updatePreset(preset.id, body);
        notifySuccess(`Saved ${body.name}`, { title: "Discount updated" });
      } else {
        await createPreset(body);
        notifySuccess(`Added ${body.name}`, { title: "Discount added" });
      }
      onClose();
    } catch (error) {
      if (isDuplicatePresetNameError(error)) {
        form.setFieldError(
          "name",
          `A discount named "${body.name}" already exists.`,
        );
      } else {
        setSubmitError(
          (error as Error | null)?.message ||
            "Failed to save discount. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={preset ? "Edit Discount" : "Add Discount"}
      size="md"
      fullScreen={isMobile}
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
        <Stack gap="md">
          {submitError && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="var(--color-danger)"
            >
              {submitError}
            </Alert>
          )}

          <TextInput
            label="Name"
            placeholder="e.g. Birthday"
            required
            maxLength={MAX_DISCOUNT_PRESET_NAME_LENGTH}
            data-autofocus
            {...form.getInputProps("name")}
          />

          <DiscountValueFields
            type={form.values.discount_type}
            amount={form.values.amount}
            onTypeChange={(type) => form.setFieldValue("discount_type", type)}
            onAmountChange={(amount) => form.setFieldValue("amount", amount)}
            amountError={form.errors.amount}
          />

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="var(--brand-teal)"
              loading={isSubmitting}
            >
              {preset ? "Save Changes" : "Add Discount"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
